/**
 * Hook centralizzato per i gate basati sul ruolo organization.
 *
 * Riflette la matrice permessi documentata in `Volt/03_FEATURES/team-roles-matrix.md`.
 * Tutti i componenti che fanno gate UI per ruolo devono usare questo hook invece di
 * controllare `currentRole === 'owner'` sparso.
 *
 * Gli scope "🔒 solo i propri record" (es. TEC modifica solo i suoi customers) sono
 * gestiti separatamente da una helper `isOwnRecord(record)` perché richiedono accesso
 * al record specifico.
 *
 * Modello dati: `organization_role` enum: owner | admin | supervisor | technician | viewer
 */
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';

export type OrgRole = 'owner' | 'admin' | 'supervisor' | 'technician' | 'viewer';

export interface RoleGates {
  // ---- ruolo corrente ----
  role: OrgRole | null;
  isOwner: boolean;
  isAdmin: boolean;
  isSupervisor: boolean;
  isTechnician: boolean;
  isViewer: boolean;

  // ---- A. Clienti ----
  canCreateCustomer: boolean;
  canModifyAnyCustomer: boolean; // se false ma TEC, può modificare solo i propri (isOwnRecord)
  canDeleteCustomer: boolean;

  // ---- B. Progetti ----
  canCreateProject: boolean;
  canModifyAnyProject: boolean;
  canDeleteProject: boolean;
  canChangeProjectStatus: boolean;

  // ---- C. Preventivi ----
  canCreateEstimate: boolean;
  canModifyAnyEstimate: boolean;
  canDeleteEstimate: boolean;
  canExportEstimatePDF: boolean;

  // ---- D. Stratigrafie ----
  canCreateStratigraphy: boolean;
  canModifyAnyStratigraphy: boolean;
  canDeleteStratigraphy: boolean;
  canCertifyStratigraphy: boolean; // toggle is_certified

  // ---- E. Materiali / Catalogo ----
  canCreateCustomMaterial: boolean;
  canModifyAnyCustomMaterial: boolean;
  canDeleteCustomMaterial: boolean;
  canImportListino: boolean;
  canEditSupplierDiscounts: boolean;
  canBulkEditPrices: boolean;

  // ---- F. Team ----
  canSeeMembers: boolean;
  canInviteMember: boolean;
  canChangeMemberRole: boolean;
  canRemoveMember: boolean;

  // ---- G. Settings tecniche cantiere ----
  canSeeTechnicalSettings: boolean;
  canEditTechnicalSettings: boolean;

  // ---- H. Billing ----
  canSeeBilling: boolean;
  canChangePlan: boolean;
  canOpenCustomerPortal: boolean;
}

const ALLOWED_TO_MANAGE_CONFIG: ReadonlySet<OrgRole> = new Set(['owner', 'admin', 'supervisor']);
const ALLOWED_TO_MANAGE_TEAM:   ReadonlySet<OrgRole> = new Set(['owner', 'admin']);
const ALLOWED_TO_DELETE:        ReadonlySet<OrgRole> = new Set(['owner', 'admin', 'supervisor']);
const ALLOWED_TO_CERTIFY:       ReadonlySet<OrgRole> = new Set(['owner', 'admin', 'supervisor']);
const ALLOWED_TO_CREATE:        ReadonlySet<OrgRole> = new Set(['owner', 'admin', 'supervisor', 'technician']);

/**
 * Hook principale: ritorna i gate per il ruolo corrente.
 */
export function useRoleGates(): RoleGates {
  const { currentRole } = useCurrentOrganization();
  const role = (currentRole ?? null) as OrgRole | null;

  const isOwner      = role === 'owner';
  const isAdmin      = role === 'admin';
  const isSupervisor = role === 'supervisor';
  const isTechnician = role === 'technician';
  const isViewer     = role === 'viewer';

  const canManageConfig = role ? ALLOWED_TO_MANAGE_CONFIG.has(role) : false;
  const canManageTeam   = role ? ALLOWED_TO_MANAGE_TEAM.has(role) : false;
  const canDelete       = role ? ALLOWED_TO_DELETE.has(role) : false;
  const canCertify      = role ? ALLOWED_TO_CERTIFY.has(role) : false;
  const canCreate       = role ? ALLOWED_TO_CREATE.has(role) : false;

  return {
    role, isOwner, isAdmin, isSupervisor, isTechnician, isViewer,

    // A. Clienti
    canCreateCustomer:        canCreate,
    canModifyAnyCustomer:     canManageConfig, // SUP+ modifica tutti; TEC solo i suoi via isOwnRecord
    canDeleteCustomer:        canDelete,

    // B. Progetti
    canCreateProject:         canCreate,
    canModifyAnyProject:      canManageConfig,
    canDeleteProject:         canDelete,
    canChangeProjectStatus:   canManageConfig, // TEC può solo sui suoi

    // C. Preventivi
    canCreateEstimate:        canCreate,
    canModifyAnyEstimate:     canManageConfig,
    canDeleteEstimate:        canDelete,
    canExportEstimatePDF:     !!role, // tutti (incluso viewer)

    // D. Stratigrafie
    canCreateStratigraphy:    canCreate,
    canModifyAnyStratigraphy: canManageConfig,
    canDeleteStratigraphy:    canDelete,
    canCertifyStratigraphy:   canCertify,

    // E. Materiali
    canCreateCustomMaterial:  canCreate,
    canModifyAnyCustomMaterial: canManageConfig,
    canDeleteCustomMaterial:  canDelete,
    canImportListino:         canManageConfig,
    canEditSupplierDiscounts: canManageConfig,
    canBulkEditPrices:        canManageConfig,

    // F. Team
    canSeeMembers:            !!role,
    canInviteMember:          canManageTeam,
    canChangeMemberRole:      canManageTeam,
    canRemoveMember:          canManageTeam,

    // G. Settings tecniche
    canSeeTechnicalSettings:  !!role,
    canEditTechnicalSettings: canManageConfig,

    // H. Billing
    canSeeBilling:            isOwner,
    canChangePlan:            isOwner,
    canOpenCustomerPortal:    isOwner,
  };
}

/**
 * Helper: ritorna true se il record è stato creato dall'utente corrente.
 * Usato in coppia con i gate "canModifyAny*" — se il gate è false MA isOwnRecord
 * è true, l'utente può comunque modificare il record (regola TEC scoped).
 */
export function useIsOwnRecord(record: { created_by?: string | null; user_id?: string | null } | null | undefined): boolean {
  const { user } = useAuth();
  if (!record || !user) return false;
  const recordOwnerId = record.created_by ?? record.user_id ?? null;
  return recordOwnerId === user.id;
}

/**
 * Hook composto: ritorna true se l'utente può modificare il record specifico
 * (combinazione gate ruolo + ownership).
 *
 * @param record record con created_by (o user_id legacy)
 * @param canModifyAnyForRole se l'utente con questo ruolo può modificare TUTTI
 *                            i record del tipo (es. canModifyAnyCustomer)
 */
export function useCanModifyRecord(
  record: { created_by?: string | null; user_id?: string | null } | null | undefined,
  canModifyAnyForRole: boolean,
): boolean {
  const { user } = useAuth();
  if (!record || !user) return false;
  if (canModifyAnyForRole) return true;
  const recordOwnerId = record.created_by ?? record.user_id ?? null;
  return recordOwnerId === user.id;
}
